"use client";

import { printableArtworks } from "./hide-unprintable";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import NextLink from "next/link";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

import ArtworkCard, { TILE_ASPECT, type ArtworkCardLinks } from "src/components/art/ArtworkCard";
import QuickAddSheet from "src/components/art/QuickAddSheet";
import {
  ActiveFilterChips,
  FilterButton,
  SortMenu,
} from "src/components/art/StoreFilterBar";
import ArtSetsRow from "src/components/art/ArtSetsRow";
import { useArtworkCollections, useArtworks, useCanvasVariants } from "src/components/art/useArtwork";
import { useAddArtworkToCart } from "src/components/art/useAddArtworkToCart";
import { useWishlist } from "src/components/art/useWishlist";
import {
  localized,
  type Artwork,
  type ArtworkKind,
} from "src/components/art/artwork-types";
import { searchIndex } from "src/components/art/artwork-search";
import {
  availableMoodTags,
  availableOrientations,
  filterAndSort,
  filtersFromQuery,
  filtersToQuery,
  isFiltered,
  priceFrom,
  type StoreFilters,
} from "src/components/art/artwork-store";
import { useCardOffers } from "src/components/art/useCardOffers";
import CatalogueOptionsLine from "src/components/art/CatalogueOptionsLine";
import type { DisplayPrice } from "src/components/canvas/variant-price";
import TrustStrip from "src/components/canvas/TrustStrip";
import { FREE_SHIPPING_THRESHOLD, formatCurrency } from "@configs/currencies";
import { useMarketDomain, useShopCurrencyValue } from "src/hooks/useShopMarket";

/**
 * A gallery of ready-made pieces, laid out as a shop.
 *
 * A short heading, then one sticky toolbar — search, collections, filters,
 * order — so that on a phone the first prints are on the first screen and the
 * controls stay in reach while scrolling. Every work sits in the same 4:5
 * frame, whole and uncropped, so titles and prices line up across the grid;
 * every card carries its "from" price and a basket button that asks for the
 * finish and size in a sheet.
 *
 * The filters and the order live in the URL, so a view someone narrowed down
 * can be sent to someone else.
 */
export interface ArtGalleryProps {
  /** Which storefront this is. */
  kind: ArtworkKind;
  /** Translation keys differ only in wording, never in structure. */
  copy: { kicker: string; title: string; lede: string; empty: string };
  /** Where a piece links to: /art or /photography. */
  basePath: string;
  /** A collection page: the gallery narrowed to one collection, under its own heading. */
  collectionSlug?: string;
}

/** Typing pauses this long before the URL is rewritten. */
const SEARCH_DEBOUNCE_MS = 300;

/** The grid: two prints across a phone, three on a tablet, four on a desktop. */
const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
  columnGap: { xs: 1.5, md: 3 },
  rowGap: { xs: 3, md: 4.5 },
} as const;

/** A horizontal row that scrolls under the thumb without a scrollbar. */
const sidewaysSx = {
  display: "flex",
  gap: 1,
  overflowX: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
} as const;

export default function ArtGallery({ kind, copy, basePath, collectionSlug }: ArtGalleryProps) {
  const t = useTranslations("art");
  const tEditor = useTranslations("editor");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();
  const currency = useShopCurrencyValue();
  const marketDomain = useMarketDomain();
  // The server renders the phone's short placeholder; a desktop swaps in the
  // long one after hydration.
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const { data: artworks, isLoading, error } = useArtworks(kind);
  const { data: collections, isLoading: collectionsLoading } = useArtworkCollections();
  const { data: variants } = useCanvasVariants();
  const { saved, ids: savedIds, toggle: toggleSaved } = useWishlist();

  // One add-to-cart for the page, mounted for as long as the gallery is, so
  // the cart drawer keeps its way to checkout after the sheet has closed.
  const cart = useAddArtworkToCart(t("addToCartFailed"));
  const [quickAdd, setQuickAdd] = useState<Artwork | null>(null);
  const openQuickAdd = useCallback(
    (artwork: Artwork) => {
      cart.clearError();
      setQuickAdd(artwork);
    },
    [cart],
  );

  // The URL is the state. Reading it back on every render is what makes the
  // back button undo a filter.
  const filters = useMemo(
    () => filtersFromQuery(searchParams ?? new URLSearchParams()),
    [searchParams],
  );
  const queryString = searchParams?.toString() ?? "";

  const setFilters = useCallback(
    (next: Partial<Omit<StoreFilters, "collection">>) => {
      const qs = filtersToQuery({ ...filters, ...next });
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [filters, pathname, router],
  );

  // The box answers keystrokes at once; the URL catches up when typing pauses.
  // Compared trimmed, because the URL is: otherwise the pause after "monet "
  // would write q=monet back into the box and eat the space being typed.
  // Following the URL (back button, a cleared filter) is done while rendering
  // rather than in an effect, as React recommends for derived state.
  const [query, setQuery] = useState(filters.query);
  const [seenUrlQuery, setSeenUrlQuery] = useState(filters.query);
  if (seenUrlQuery !== filters.query) {
    setSeenUrlQuery(filters.query);
    if (query.trim() !== filters.query) setQuery(filters.query);
  }
  useEffect(() => {
    if (query.trim() === filters.query) return;
    const timer = setTimeout(() => setFilters({ query }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, filters.query, setFilters]);

  // Built once per work, not once per keystroke.
  const indexes = useMemo(() => {
    const map = new Map<string, string>();
    for (const artwork of artworks ?? []) map.set(artwork.id, searchIndex(artwork));
    return map;
  }, [artworks]);

  // Works nothing can print are not offered at all. A near-square painting has
  // no matching format while the catalogue stocks only landscape shapes, and
  // showing it led to a page whose only content was "no size available".
  // Computed, so a work returns by itself when a format it fits is added.
  const buyable = useMemo(
    () => printableArtworks(artworks ?? [], variants),
    [artworks, variants],
  );

  // The true lowest price per work, for the price sort; worked out once
  // rather than per comparison. The cards quote per material instead.
  const prices = useMemo(() => {
    const map = new Map<string, DisplayPrice | null>();
    for (const artwork of buyable) map.set(artwork.id, priceFrom(artwork, variants, currency));
    return map;
  }, [buyable, variants, currency]);
  // Per material "from" lines, the size range and the hover room's size.
  const offersOf = useCardOffers(buyable, variants);

  // A collection with nothing in this storefront is a link to an empty page —
  // the art collections have no photographs in them and vice versa. Judged on
  // what is buyable, so a collection whose every work is unprintable does not
  // sit there leading nowhere.
  const relevant = useMemo(() => {
    if (!collections) return [];
    return collections.filter((c) =>
      buyable.some((a) => (a.collections ?? []).some((x) => x.slug === c.slug)),
    );
  }, [collections, buyable]);

  const collection = collectionSlug
    ? collections?.find((c) => c.slug === collectionSlug)
    : undefined;

  const inScope = useMemo(
    () =>
      collectionSlug
        ? buyable.filter((a) => (a.collections ?? []).some((c) => c.slug === collectionSlug))
        : buyable,
    [buyable, collectionSlug],
  );

  const liveFilters = { ...filters, query, collection: null };
  const shown = useMemo(
    () =>
      filterAndSort(
        inScope,
        { ...filters, query, collection: null },
        {
          indexOf: (a) => indexes.get(a.id) ?? "",
          priceOf: (a) => prices.get(a.id)?.amount ?? null,
          savedIds: saved,
        },
      ),
    [inScope, filters, query, indexes, prices, saved],
  );

  const orientations = useMemo(() => availableOrientations(inScope), [inScope]);
  const moodTags = useMemo(() => availableMoodTags(inScope), [inScope]);

  const localeSegment = (params?.locale as string) ?? locale;
  const root = `/${localeSegment}/${basePath}`;
  // Moving between collections keeps the shape, mood and order already chosen.
  const withQuery = (path: string) => (queryString ? `${path}?${queryString}` : path);
  const hrefOf = useCallback((artwork: Artwork) => `${root}/${artwork.slug}`, [root]);

  const cardProps = (artwork: Artwork): ArtworkCardLinks => ({
    href: hrefOf(artwork),
    locale,
    ...offersOf(artwork),
    onQuickAdd: openQuickAdd,
    saved: saved.has(artwork.id),
    onToggleSaved: () => toggleSaved(artwork.id),
  });

  // On a phone the chips scroll sideways; bring the current collection into
  // view, or a page opened on the fifth one shows no chip as selected.
  const chipsRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const row = chipsRef.current;
    const current = row?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!row || !current || row.scrollWidth <= row.clientWidth) return;
    row.scrollLeft = current.offsetLeft - (row.clientWidth - current.offsetWidth) / 2;
  }, [collectionSlug, relevant.length]);

  const unknownCollection =
    !!collectionSlug && !collectionsLoading && !!collections && !collection;

  const heading = collection ? localized(collection.title, locale) : t(copy.title);
  const lede = collection
    ? localized(collection.description, locale) ||
      (t.has(`collectionIntros.${collection.slug}`)
        ? t(`collectionIntros.${collection.slug}`)
        : t("collectionIntro", { name: heading }))
    : t(copy.lede);

  const clearAll = () => {
    setQuery("");
    router.replace(pathname, { scroll: false });
  };

  // Only claims the shop already makes elsewhere: the market's free-delivery
  // line from the same table the cart uses (none configured, none claimed),
  // the delivery time from the artwork page, and the canvas editor's colour
  // promise, which holds for every material.
  const thresholdCents = (FREE_SHIPPING_THRESHOLD as Record<string, number | undefined>)[marketDomain] ?? 0;
  const trustLines = [
    ...(thresholdCents > 0
      ? [t("freeShippingOver", { amount: formatCurrency(thresholdCents / 100, currency) })]
      : []),
    t("delivery"),
    tEditor("canvasTrustQuality"),
  ];

  const tt = t as unknown as (key: string, values?: Record<string, string | number>) => string;
  const ready = !isLoading && !error;

  const collectionChips = (
    <>
      <Chip
        component={NextLink}
        href={withQuery(root)}
        clickable
        label={t("allWorks")}
        color={!collectionSlug ? "primary" : "default"}
        variant={!collectionSlug ? "filled" : "outlined"}
        aria-current={!collectionSlug ? "page" : undefined}
        sx={{ flexShrink: 0, fontWeight: 600 }}
      />
      {relevant.map((c) => (
        <Chip
          key={c.id}
          component={NextLink}
          href={withQuery(`${root}/zbirka/${c.slug}`)}
          clickable
          label={localized(c.title, locale)}
          color={collectionSlug === c.slug ? "primary" : "default"}
          variant={collectionSlug === c.slug ? "filled" : "outlined"}
          aria-current={collectionSlug === c.slug ? "page" : undefined}
          sx={{ flexShrink: 0, fontWeight: 600 }}
        />
      ))}
    </>
  );

  const savedChip = (
    <Chip
      icon={filters.saved ? <FavoriteIcon /> : <FavoriteBorderIcon />}
      label={t("wishlistChip", { count: savedIds.length })}
      onClick={() => setFilters({ saved: !filters.saved })}
      aria-pressed={filters.saved}
      color={filters.saved ? "primary" : "default"}
      variant={filters.saved ? "filled" : "outlined"}
      sx={{ flexShrink: 0, fontWeight: 600 }}
    />
  );

  // On a desktop the hearts are a button in the toolbar, with their count.
  const savedButton = (
    <Badge color="primary" badgeContent={savedIds.length} invisible={!savedIds.length}>
      <IconButton
        onClick={() => setFilters({ saved: !filters.saved })}
        aria-pressed={filters.saved}
        aria-label={t("wishlistChip", { count: savedIds.length })}
        sx={(theme) => ({
          width: 40,
          height: 40,
          border: 1,
          borderColor: filters.saved ? theme.tokens.action : "divider",
          color: filters.saved ? theme.tokens.action : "text.primary",
          bgcolor: filters.saved ? theme.tokens.brandSoft : "transparent",
        })}
      >
        {filters.saved ? <FavoriteIcon sx={{ fontSize: 20 }} /> : <FavoriteBorderIcon sx={{ fontSize: 20 }} />}
      </IconButton>
    </Badge>
  );

  return (
    <>
      <Box sx={{ bgcolor: "background.default", pt: { xs: 2, md: 4 }, pb: { xs: 1.5, md: 2.5 } }}>
        <Container maxWidth="lg">
          {collection || collectionSlug ? (
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: ".12em", lineHeight: 1.6 }}>
              <Link component={NextLink} href={withQuery(root)} color="inherit" underline="hover">
                {t(copy.kicker)}
              </Link>
            </Typography>
          ) : null}
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, fontSize: { xs: 24, md: 34 }, lineHeight: 1.2 }}
          >
            {collectionSlug && !collection ? " " : heading}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 0.75, maxWidth: "72ch", fontSize: { xs: 14, md: 16 } }}
          >
            {collectionSlug && !collection ? "" : lede}{" "}
            {/* Someone who landed here from search may actually want their
                own picture on the wall. Do not make them find the menu. */}
            <Link
              component={NextLink}
              href={`/${localeSegment}/wall-art`}
              sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
            >
              {t("orUseOwnPhoto")} →
            </Link>
          </Typography>
          <CatalogueOptionsLine variants={variants} />
        </Container>
      </Box>

      {/* Collections on a phone: a row of their own that scrolls sideways,
          above the toolbar and out of the way once the list is scrolled. On
          a desktop they sit in the toolbar itself. */}
      <Container maxWidth="lg" sx={{ display: { xs: "block", md: "none" } }}>
        <Box
          ref={chipsRef}
          component="nav"
          aria-label={t("collectionsNav")}
          sx={{ ...sidewaysSx, mx: -2, px: 2, pb: 1 }}
        >
          {collectionChips}
          {savedChip}
        </Box>
      </Container>

      {/* The one toolbar: stays at the top of the screen while scrolling. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg" sx={{ py: 1 }}>
          <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} alignItems="center">
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(desktop ? "searchPlaceholder" : "searchShort")}
              size="small"
              type="search"
              inputProps={{ "aria-label": t("searchPlaceholder") }}
              sx={{
                flex: { xs: 1, md: "0 0 296px" },
                minWidth: 0,
                "& .MuiOutlinedInput-root": { borderRadius: 999, height: 40, fontSize: 15 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={t("searchClear")}
                      onClick={() => {
                        setQuery("");
                        setFilters({ query: "" });
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Box
              component="nav"
              aria-label={t("collectionsNav")}
              sx={{
                ...sidewaysSx,
                display: { xs: "none", md: "flex" },
                flex: 1,
                minWidth: 0,
                // A soft edge where the row runs out of room, instead of a
                // chip sliced in half.
                maskImage: "linear-gradient(to right, #000 calc(100% - 24px), transparent)",
                WebkitMaskImage: "linear-gradient(to right, #000 calc(100% - 24px), transparent)",
                pr: 3,
              }}
            >
              {collectionChips}
            </Box>
            <FilterButton
              filters={liveFilters}
              onChange={setFilters}
              orientations={orientations}
              moodTags={moodTags}
              count={shown.length}
              t={tt}
            />
            <SortMenu sort={filters.sort} onChange={(sort) => setFilters({ sort })} t={tt} />
            <Box sx={{ display: { xs: "none", md: "block" } }}>{savedButton}</Box>
            {ready && (
              <Typography
                variant="body2"
                color="text.secondary"
                aria-live="polite"
                sx={{ whiteSpace: "nowrap", fontSize: { xs: 13, md: 14 }, minWidth: { md: 64 }, textAlign: "right" }}
              >
                {t("resultCount", { count: shown.length })}
              </Typography>
            )}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pt: { xs: 1.5, md: 2 }, pb: { xs: 5, md: 7 } }}>
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <TrustStrip lines={trustLines} />
        </Box>

        {unknownCollection ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            {t("collectionNotFound")}{" "}
            <Link component={NextLink} href={root}>
              {t("backToGallery")}
            </Link>
          </Alert>
        ) : null}

        {isFiltered(liveFilters) && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2.5, flexWrap: "wrap", rowGap: 1 }}
          >
            <ActiveFilterChips
              filters={liveFilters}
              onChange={setFilters}
              onClearAll={clearAll}
              t={tt}
            />
          </Stack>
        )}

        {isLoading && (
          <Box sx={gridSx} aria-busy>
            {Array.from({ length: 8 }, (_, i) => (
              <Box key={i}>
                <Skeleton
                  variant="rounded"
                  sx={{ width: "100%", height: "auto", aspectRatio: String(TILE_ASPECT) }}
                />
                <Skeleton width="50%" sx={{ mt: 1.25 }} />
                <Skeleton width="80%" />
                <Skeleton width="35%" />
              </Box>
            ))}
          </Box>
        )}

        {!!error && <Alert severity="error">{t("loadFailed")}</Alert>}

        {/* An empty result from a search or a filter is a different situation
            from an empty storefront, and needs a different sentence — and a
            way out. Hearts are their own case: an empty list there means
            nothing has been saved yet, so say how. */}
        {ready && !unknownCollection && shown.length === 0 && (
          <Alert
            severity="info"
            action={
              isFiltered(liveFilters) ? (
                <Button size="small" color="inherit" onClick={clearAll} sx={{ textTransform: "none" }}>
                  {t("clearFilters")}
                </Button>
              ) : undefined
            }
          >
            {filters.saved && !savedIds.length
              ? t("wishlistEmpty")
              : query
                ? t("searchNoResults")
                : isFiltered(liveFilters)
                  ? t("filterNoResults")
                  : t(copy.empty)}
          </Alert>
        )}

        {ready && shown.length > 0 && (
          <Box sx={gridSx}>
            {shown.map((artwork) => (
              <ArtworkCard key={artwork.id} artwork={artwork} {...cardProps(artwork)} />
            ))}
          </Box>
        )}

        {/* Curated sets (art-sets.ts): one calm section below the grid, on
            the art gallery itself, not on collection pages. */}
        {!isLoading && !error && kind === "ART" && !collectionSlug && <ArtSetsRow />}
      </Container>

      <QuickAddSheet
        artwork={quickAdd}
        onClose={() => setQuickAdd(null)}
        hrefOf={hrefOf}
        onAdd={cart.add}
        adding={cart.adding}
        error={cart.error}
      />
      {/* An add that failed after the sheet was closed still needs saying. */}
      <Snackbar
        open={!!cart.error && !quickAdd}
        onClose={cart.clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={cart.clearError}>
          {cart.error}
        </Alert>
      </Snackbar>
    </>
  );
}
